import React from 'react';
import { Immutable } from 'nuclear-js';
import moment from 'moment';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';

import CommonStrings from 'resources/common-strings';
import RuntimeActions from 'data/app-runtime/actions';
import GrowthUtils from './utils';
import TermUtils from 'utils/terms';
import { latestItemByTerm } from 'data/utils';

export default CreateReactClass({
    displayName: 'CurrentGoals',

    propTypes: {
        currentGrowthGoals: PropTypes.instanceOf(Immutable.List),
        firstOpenTerm: PropTypes.instanceOf(Immutable.Map),
        testEvents: PropTypes.instanceOf(Immutable.List),
        goalSettingTerms: PropTypes.instanceOf(Immutable.List).isRequired,
    },

    onExpand() {
        RuntimeActions.expandModule('growth-goals');
    },

    renderWithNoGoalsSet() {
        if (!this.props.firstOpenTerm) {
            return <p className="text-bold">{CommonStrings.nextTermNotSetMessage}</p>;
        }
        const goalMessage = GrowthUtils.canSetGrowthGoal(this.props.testEvents, this.props.firstOpenTerm)
            ? 'Customize the growth target for this student by setting a growth goal '
            : 'Set a performance goal for this student ';
        const mostRecentTestEvent = latestItemByTerm(this.props.testEvents);
        const shouldUseGoalSettingTerms =
            mostRecentTestEvent &&
            mostRecentTestEvent.get('isCourseSpecific') &&
            this.props.goalSettingTerms &&
            !this.props.goalSettingTerms.isEmpty();
        const termToUseForName = shouldUseGoalSettingTerms
            ? latestItemByTerm(this.props.goalSettingTerms)
            : this.props.firstOpenTerm;
        const termName = TermUtils.textFromTermNumber(termToUseForName.get('termNumber'), true);
        return (
            <div className="no-goal-wrapper">
                <p key="term-number">{termName}</p>
                <div key="no-goal-clickable-fields" className="goal-clickable-fields" onClick={this.onExpand}>
                    <span key="target-icon" className="icon icon-bullseye" />
                    <span key="ia-goal-message" className="ia-goal-message">
                        {goalMessage}
                    </span>
                    <span key="ia-arrow" className="icon icon-action-arrow-right ia-arrow"></span>
                </div>
            </div>
        );
    },

    renderGrowthGoal(goal) {
        // these should always be set for growth goals
        const targetScore = goal.get('targetScore');
        const startTermScore = goal.get('startTermScore');
        const targetGrowth = targetScore - startTermScore;
        const sign = targetGrowth >= 0 ? '+' : '';
        const startTermName = TermUtils.textFromTermNumber(goal.get('startTermNumber'), true);
        const endTermName = TermUtils.textFromTermNumber(goal.get('endTermNumber'), true);

        return (
            <div key="current-goals" className="growth-goal-wrapper">
                <span className="growth-goal-module-info">
                    <p className="term-name">{endTermName} GOAL</p>
                    <span className="text-growth-rit">{targetScore}</span>
                    <span className="text-growth-goal">{`(${sign}${targetGrowth})`}</span>
                </span>
                <span className="score-label">
                    <p>Score when set:</p>
                    <p className="start-term">{`(${startTermName})`}</p>
                </span>
                <span className="growth-goal-text-score">{startTermScore}</span>
            </div>
        );
    },

    renderPerformanceGoal(goal) {
        const endTermName = TermUtils.textFromTermNumber(goal.get('endTermNumber'), true);
        const typicalScore = goal.get('typicalScore')
            ? goal.get('typicalScore')
            : `${CommonStrings.space}${CommonStrings.blanks}`;

        return (
            <div key="performance-goal" className="performance-goal-wrapper">
                <span className="performance-score" key="performance-score">
                    <p className="term-name">{endTermName} GOAL</p>
                    <span className="text-rit">{goal.get('targetScore')}</span>
                </span>
                <span className="performance-summary" key="performance-summary">
                    <p>Performance Goal</p>
                    <p>Set on {moment(new Date(goal.get('dateEdited'))).format('l')}</p>
                    <p>Average achievement: {typicalScore}</p>
                </span>
            </div>
        );
    },

    renderWithAlreadyAchievedMessage(goal, testEvent) {
        const endTermName = TermUtils.textFromTermNumber(goal.get('endTermNumber'), true);
        const message = `${endTermName} goal of ${goal.get(
            'targetScore'
        )} was already met in ${TermUtils.textFromTermNumber(testEvent.get('termNumber'), true)}. Review this goal?`;

        return (
            <div
                key="already-achieved-clickable-fields"
                className="goal-clickable-fields goal-already-achieved"
                onClick={this.onExpand}
            >
                <span key="target-icon" className="icon icon-bullseye" />
                <span key="ia-goal-message" className="ia-goal-message">
                    {message}
                </span>
                <span key="ia-arrow" className="icon icon-action-arrow-right ia-arrow"></span>
            </div>
        );
    },

    renderWithMoreGoalsMessage(node, currentGrowthGoals) {
        return (
            <div>
                {node}
                <p key="more-goals" className="more-goals" onClick={this.onExpand}>
                    +{currentGrowthGoals.size - 1} more goal(s)
                </p>
            </div>
        );
    },

    renderWithNoNextTermMessage() {
        return <p className="next-term-not-set">{CommonStrings.nextTermNotSetMessage}</p>;
    },

    renderWithNoGoalSettingTermsMessage() {
        return <p className="next-term-not-set">{CommonStrings.noGoalSettingTerms}</p>;
    },

    render() {
        if (this.props.goalSettingTerms.isEmpty()) {
            return this.renderWithNoGoalSettingTermsMessage();
        } else if (!this.props.firstOpenTerm) {
            return this.renderWithNoNextTermMessage();
        }

        if (!this.props.currentGrowthGoals) {
            return null;
        }

        let goalNode = null;
        const goal = this.props.currentGrowthGoals.sortBy((g) => g.get('endTermNumber')).first();
        const testEventAchievingGoal = GrowthUtils.testEventAchievingGoal(goal, this.props.testEvents);
        if (!goal) {
            goalNode = this.renderWithNoGoalsSet();
        } else if (testEventAchievingGoal) {
            goalNode = this.renderWithAlreadyAchievedMessage(goal, testEventAchievingGoal);
        } else if (GrowthUtils.isGrowthGoal(goal)) {
            goalNode = this.renderGrowthGoal(goal);
        } else {
            goalNode = this.renderPerformanceGoal(goal);
        }

        if (this.props.currentGrowthGoals.size > 1) {
            goalNode = this.renderWithMoreGoalsMessage(goalNode, this.props.currentGrowthGoals);
        }

        return goalNode;
    },
});
